import { ActionType, createReducer } from 'typesafe-actions';
import { ExecutiveStore } from './executive.types';
import * as actions from './executive.action';
import * as types from './executive.actionTypes';
import { DashboardTabs } from 'pages/dashboardRevamp/dashboardUtils';
import { v4 as uuidv4 } from 'uuid';

import moment from 'moment';
import { formatDateSafe } from 'utils/dates';
const initialAPIData = {
	isLoading: false,
	isSuccess: false,
	data: null,
};

const initialPerformanceData = {
	data: null,
	isLoading: false,
};

const initialState: ExecutiveStore = {
	type: DashboardTabs.Issues,
	showMap: false,
	showFilters: false,
	filters: {
		startDate: moment().subtract(7, 'days').format('YYYY-MM-DD'),
		endDate: moment().format('YYYY-MM-DD'),
		sites: [],
		departments: [],
		users: [],
		questionnaires: [],
		categories: [],
		roles: [],
	},
	departmentRatio: initialAPIData,
	irrRatio: initialAPIData,
	rcrRatio: initialAPIData,
	vocRatio: initialAPIData,
	heatmapIssueFlags: initialAPIData,
	// PerfomanceCard data - Issues tab
	performanceIssuesBySite: initialPerformanceData,
	performanceIssuesByUser: initialPerformanceData,
	performanceIssuesByDepartment: initialPerformanceData,
	performanceIssuesByCategory: initialPerformanceData,
	// PerfomanceCard data - Reports tab
	performanceReportsBySite: initialPerformanceData,
	performanceReportsByUser: initialPerformanceData,
	performanceReportsByDepartment: initialPerformanceData,
	// HealthScore data
	healthScoreBySite: initialPerformanceData,
	healthScoreByUser: initialPerformanceData,
	healthScoreByDepartment: initialPerformanceData,
	healthScoreByCategory: initialPerformanceData,
	searchTrigger: '',
	lastUpdated: moment().format('HH:mm'),
	lastSelectedWidget: {
		issuesConfig: null,
		reportsConfig: null,
	},
};

export type ExecutiveAction = ActionType<typeof actions>;

export const executiveDashboardReducer = createReducer<ExecutiveStore, ExecutiveAction>(initialState)
	.handleAction(actions.fetchDepartmentRatioAsync.request, (state) => ({
		...state,
		departmentRatio: {
			data: null,
			isLoading: true,
			isSuccess: true,
		},
	}))
	.handleAction(actions.fetchDepartmentRatioAsync.success, (state, action) => ({
		...state,
		departmentRatio: {
			data: action.payload || '',
			isLoading: false,
			isSuccess: false,
		},
	}))
	.handleAction(actions.fetchDepartmentRatioAsync.failure, (state, action) => ({
		...state,
		...state,
		departmentRatio: {
			data: null,
			isLoading: false,
			isSuccess: false,
		},
	}))
	.handleAction(actions.fetchIrrRatioAsync.request, (state) => ({
		...state,
		irrRatio: {
			data: null,
			isLoading: true,
			isSuccess: true,
		},
	}))
	.handleAction(actions.fetchIrrRatioAsync.success, (state, action) => ({
		...state,
		irrRatio: {
			data: action.payload,
			isLoading: false,
			isSuccess: false,
		},
	}))
	.handleAction(actions.fetchIrrRatioAsync.failure, (state, action) => ({
		...state,
		...state,
		irrRatio: {
			data: null,
			isLoading: false,
			isSuccess: false,
		},
	}))

	.handleAction(actions.fetchRcrRatioAsync.request, (state) => ({
		...state,
		rcrRatio: {
			data: null,
			isLoading: true,
			isSuccess: true,
		},
	}))
	.handleAction(actions.fetchRcrRatioAsync.success, (state, action) => ({
		...state,
		rcrRatio: {
			data: action.payload,
			isLoading: false,
			isSuccess: false,
		},
	}))
	.handleAction(actions.fetchRcrRatioAsync.failure, (state, action) => ({
		...state,
		...state,
		rcrRatio: {
			data: null,
			isLoading: false,
			isSuccess: false,
		},
	}))

	.handleAction(actions.fetchVocRatioAsync.request, (state) => ({
		...state,
		vocRatio: {
			data: null,
			isLoading: true,
			isSuccess: true,
		},
	}))
	.handleAction(actions.fetchVocRatioAsync.success, (state, action) => ({
		...state,
		vocRatio: {
			data: action.payload,
			isLoading: false,
			isSuccess: false,
		},
	}))
	.handleAction(actions.fetchVocRatioAsync.failure, (state, action) => ({
		...state,
		...state,
		vocRatio: {
			data: null,
			isLoading: false,
			isSuccess: false,
		},
	}))
	.handleAction(actions.fetchHeatmapIssueFlagsAsync.request, (state) => ({
		...state,
		heatmapIssueFlags: {
			data: null,
			isLoading: true,
			isSuccess: true,
		},
	}))
	.handleAction(actions.fetchHeatmapIssueFlagsAsync.success, (state, action) => ({
		...state,
		heatmapIssueFlags: {
			data: action.payload,
			isLoading: false,
			isSuccess: false,
		},
	}))
	.handleAction(actions.fetchHeatmapIssueFlagsAsync.failure, (state, action) => ({
		...state,
		...state,
		heatmapIssueFlags: {
			data: null,
			isLoading: false,
			isSuccess: false,
		},
	}))
	// PerfomanceCard Issues by Site handlers
	.handleAction(actions.fetchPerformanceIssuesBySiteAsync.request, (state) => ({
		...state,
		performanceIssuesBySite: { data: null, isLoading: true },
	}))
	.handleAction(actions.fetchPerformanceIssuesBySiteAsync.success, (state, action) => ({
		...state,
		performanceIssuesBySite: { data: action.payload, isLoading: false },
	}))
	.handleAction(actions.fetchPerformanceIssuesBySiteAsync.failure, (state) => ({
		...state,
		performanceIssuesBySite: { data: null, isLoading: false },
	}))
	// PerfomanceCard Issues by User handlers
	.handleAction(actions.fetchPerformanceIssuesByUserAsync.request, (state) => ({
		...state,
		performanceIssuesByUser: { data: null, isLoading: true },
	}))
	.handleAction(actions.fetchPerformanceIssuesByUserAsync.success, (state, action) => ({
		...state,
		performanceIssuesByUser: { data: action.payload, isLoading: false },
	}))
	.handleAction(actions.fetchPerformanceIssuesByUserAsync.failure, (state) => ({
		...state,
		performanceIssuesByUser: { data: null, isLoading: false },
	}))
	// PerfomanceCard Issues by Department handlers
	.handleAction(actions.fetchPerformanceIssuesByDepartmentAsync.request, (state) => ({
		...state,
		performanceIssuesByDepartment: { data: null, isLoading: true },
	}))
	.handleAction(actions.fetchPerformanceIssuesByDepartmentAsync.success, (state, action) => ({
		...state,
		performanceIssuesByDepartment: { data: action.payload, isLoading: false },
	}))
	.handleAction(actions.fetchPerformanceIssuesByDepartmentAsync.failure, (state) => ({
		...state,
		performanceIssuesByDepartment: { data: null, isLoading: false },
	}))
	// PerfomanceCard Issues by Category handlers
	.handleAction(actions.fetchPerformanceIssuesByCategoryAsync.request, (state) => ({
		...state,
		performanceIssuesByCategory: { data: null, isLoading: true },
	}))
	.handleAction(actions.fetchPerformanceIssuesByCategoryAsync.success, (state, action) => ({
		...state,
		performanceIssuesByCategory: { data: action.payload, isLoading: false },
	}))
	.handleAction(actions.fetchPerformanceIssuesByCategoryAsync.failure, (state) => ({
		...state,
		performanceIssuesByCategory: { data: null, isLoading: false },
	}))
	// PerfomanceCard Reports by Site handlers
	.handleAction(actions.fetchPerformanceReportsBySiteAsync.request, (state) => ({
		...state,
		performanceReportsBySite: { data: null, isLoading: true },
	}))
	.handleAction(actions.fetchPerformanceReportsBySiteAsync.success, (state, action) => ({
		...state,
		performanceReportsBySite: { data: action.payload, isLoading: false },
	}))
	.handleAction(actions.fetchPerformanceReportsBySiteAsync.failure, (state) => ({
		...state,
		performanceReportsBySite: { data: null, isLoading: false },
	}))
	// PerfomanceCard Reports by User handlers
	.handleAction(actions.fetchPerformanceReportsByUserAsync.request, (state) => ({
		...state,
		performanceReportsByUser: { data: null, isLoading: true },
	}))
	.handleAction(actions.fetchPerformanceReportsByUserAsync.success, (state, action) => ({
		...state,
		performanceReportsByUser: { data: action.payload, isLoading: false },
	}))
	.handleAction(actions.fetchPerformanceReportsByUserAsync.failure, (state) => ({
		...state,
		performanceReportsByUser: { data: null, isLoading: false },
	}))
	// PerfomanceCard Reports by Department handlers
	.handleAction(actions.fetchPerformanceReportsByDepartmentAsync.request, (state) => ({
		...state,
		performanceReportsByDepartment: { data: null, isLoading: true },
	}))
	.handleAction(actions.fetchPerformanceReportsByDepartmentAsync.success, (state, action) => ({
		...state,
		performanceReportsByDepartment: { data: action.payload, isLoading: false },
	}))
	.handleAction(actions.fetchPerformanceReportsByDepartmentAsync.failure, (state) => ({
		...state,
		performanceReportsByDepartment: { data: null, isLoading: false },
	}))
	// HealthScore by Site handlers
	.handleAction(actions.fetchHealthScoreBySiteAsync.request, (state) => ({
		...state,
		healthScoreBySite: { data: null, isLoading: true },
	}))
	.handleAction(actions.fetchHealthScoreBySiteAsync.success, (state, action) => ({
		...state,
		healthScoreBySite: { data: action.payload, isLoading: false },
	}))
	.handleAction(actions.fetchHealthScoreBySiteAsync.failure, (state) => ({
		...state,
		healthScoreBySite: { data: null, isLoading: false },
	}))
	// HealthScore by User handlers
	.handleAction(actions.fetchHealthScoreByUserAsync.request, (state) => ({
		...state,
		healthScoreByUser: { data: null, isLoading: true },
	}))
	.handleAction(actions.fetchHealthScoreByUserAsync.success, (state, action) => ({
		...state,
		healthScoreByUser: { data: action.payload, isLoading: false },
	}))
	.handleAction(actions.fetchHealthScoreByUserAsync.failure, (state) => ({
		...state,
		healthScoreByUser: { data: null, isLoading: false },
	}))
	// HealthScore by Department handlers
	.handleAction(actions.fetchHealthScoreByDepartmentAsync.request, (state) => ({
		...state,
		healthScoreByDepartment: { data: null, isLoading: true },
	}))
	.handleAction(actions.fetchHealthScoreByDepartmentAsync.success, (state, action) => ({
		...state,
		healthScoreByDepartment: { data: action.payload, isLoading: false },
	}))
	.handleAction(actions.fetchHealthScoreByDepartmentAsync.failure, (state) => ({
		...state,
		healthScoreByDepartment: { data: null, isLoading: false },
	}))
	// HealthScore by Category handlers
	.handleAction(actions.fetchHealthScoreByCategoryAsync.request, (state) => ({
		...state,
		healthScoreByCategory: { data: null, isLoading: true },
	}))
	.handleAction(actions.fetchHealthScoreByCategoryAsync.success, (state, action) => ({
		...state,
		healthScoreByCategory: { data: action.payload, isLoading: false },
	}))
	.handleAction(actions.fetchHealthScoreByCategoryAsync.failure, (state) => ({
		...state,
		healthScoreByCategory: { data: null, isLoading: false },
	}))
	.handleAction(types.SET_DASHBOARD_TYPE as any, (state, action) => {
		return {
			...state,
			type: action.payload,
		};
	})
	.handleAction(types.SET_SHOW_MAP as any, (state, action) => {
		return {
			...state,
			showMap: action.payload,
		};
	})
	.handleAction(types.SET_SHOW_FILTERS as any, (state, action) => {
		return {
			...state,
			showFilters: action.payload,
		};
	})
	.handleAction(types.SET_MULTI_SELECT as any, (state, action) => {
		return {
			...state,
			filters: {
				...state.filters,
				[action.payload.key]: action.payload.value,
			},
		};
	})
	.handleAction(types.SET_FILTER_DATE as any, (state, action) => {
		// Safely format dates to prevent "Invalid date" strings being stored
		// action.payload contains Moment objects or null from date picker
		const startDate = action.payload.startDate ? formatDateSafe(action.payload.startDate) : null;
		const endDate = action.payload.endDate ? formatDateSafe(action.payload.endDate) : null;
		return {
			...state,
			filters: {
				...state.filters,
				startDate,
				endDate,
			},
		};
	})
	.handleAction(types.SET_FILTERS as any, (state, action) => {
		return {
			...state,
			filters: action.payload.filters,
		};
	})
	.handleAction(types.SET_SEARCH_TRIGGER as any, (state) => {
		return {
			...state,
			searchTrigger: uuidv4(),
		};
	})
	.handleAction(types.SET_RESET_FILTER as any, (state) => {
		return {
			...state,
			filters: initialState.filters,
			searchTrigger: uuidv4(),
		};
	})

	.handleAction(types.SET_LAST_UPDATE as any, (state) => {
		return {
			...state,
			lastUpdated: moment().format('HH:mm'),
		};
	})
	.handleAction(types.SET_LAST_SELECTED_WIDGET as any, (state, action) => {
		return {
			...state,
			lastSelectedWidget: action.payload,
		};
	});
